import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as SubframeCore from '@subframe/core';
import { FeatherInfo, FeatherImage, FeatherTrendingUp, FeatherTrendingDown } from '@subframe/core';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Tabs } from '../ui/components/Tabs';
import { Badge } from '../ui/components/Badge';
import { Alert } from '../ui/components/Alert';
import { Tooltip } from '../ui/components/Tooltip';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuthStore } from '../store/authStore';
import { formatVolume } from '../utils/formatters';
import {
  getPortfolioIntelligence,
  getMarketPulse,
  type PortfolioSummary,
  type MarketPulseData,
  type MomentumLabel,
  type CollectionHolding,
  type MarketPulseCollection,
} from '../api/intelligence';

type Tab = 'portfolio' | 'market';

const momentumVariant: Record<MomentumLabel, 'success' | 'neutral' | 'warning' | 'error'> = {
  surging: 'success',
  rising: 'success',
  stable: 'neutral',
  declining: 'warning',
  crashing: 'error',
};

const sentimentVariant: Record<string, 'success' | 'neutral' | 'error'> = {
  bullish: 'success',
  neutral: 'neutral',
  bearish: 'error',
};

const sentimentDescription: Record<string, string> = {
  bullish: 'Strong buying momentum across collections',
  neutral: 'Balanced market activity',
  bearish: 'Selling pressure outweighing buyers',
};

// Health score metadata: what it means + how to improve
const healthScoreInfo: Record<string, { description: string; tip: string }> = {
  overall: {
    description: 'Weighted average of all four health dimensions.',
    tip: 'Improve by diversifying holdings, choosing liquid collections, and monitoring declining assets.',
  },
  diversification: {
    description: 'How spread out your NFTs are across collections. 100 = perfectly spread, 0 = all in one collection.',
    tip: 'Buy into more collections to reduce concentration risk.',
  },
  liquidity: {
    description: 'What percentage of your NFTs are in collections with recent sales activity in the past 7 days.',
    tip: 'Favor collections with active trading. Consider selling NFTs in collections with zero recent sales.',
  },
  momentum: {
    description: 'Value-weighted momentum of your held collections based on volume trends and floor price changes.',
    tip: 'Rebalance away from declining collections toward those with rising volume and floor prices.',
  },
  valueAtRisk: {
    description: 'Inverse of your exposure to declining or crashing collections. 100 = no declining holdings.',
    tip: 'Reduce positions in collections with negative momentum to increase this score.',
  },
};

function InfoTooltip({ text }: { text: string }) {
  return (
    <SubframeCore.Tooltip.Provider delayDuration={0}>
      <SubframeCore.Tooltip.Root>
        <SubframeCore.Tooltip.Trigger asChild>
          <button type="button" className="inline-flex items-center cursor-help">
            <FeatherInfo className="h-3.5 w-3.5 text-subtext-color" />
          </button>
        </SubframeCore.Tooltip.Trigger>
        <SubframeCore.Tooltip.Portal>
          <SubframeCore.Tooltip.Content side="bottom" align="center" sideOffset={4} asChild>
            <Tooltip className="max-w-[260px] whitespace-normal">{text}</Tooltip>
          </SubframeCore.Tooltip.Content>
        </SubframeCore.Tooltip.Portal>
      </SubframeCore.Tooltip.Root>
    </SubframeCore.Tooltip.Provider>
  );
}

function CircularGauge({ score, label, infoKey, size = 96 }: { score: number; label: string; infoKey: string; size?: number }) {
  const strokeWidth = Math.round(size * 0.073);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const strokeColor =
    score >= 70 ? 'stroke-success-600' : score >= 40 ? 'stroke-warning-600' : 'stroke-error-600';
  const textColor =
    score >= 70 ? 'text-success-600' : score >= 40 ? 'text-warning-600' : 'text-error-600';

  const info = healthScoreInfo[infoKey];

  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            className="stroke-neutral-border"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            className={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${size >= 120 ? 'text-heading-1 font-heading-1' : 'text-heading-2 font-heading-2'} ${textColor}`}>{score}</span>
        </div>
      </div>
      <span className="text-body font-body font-medium text-default-font">{label}</span>
      {info && (
        <div className="flex flex-col gap-1.5">
          <span className="text-caption font-caption text-subtext-color">{info.description}</span>
          <span className="text-caption font-caption text-subtext-color">How to improve: {info.tip}</span>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-solid border-neutral-border p-4 bg-default-background">
      <span className="text-caption font-caption text-subtext-color">{label}</span>
      {children}
    </div>
  );
}

function CollectionImage({ url, name }: { url: string | null; name: string }) {
  if (url) {
    return <img src={url} alt={name} className="w-10 h-10 rounded-md object-cover" />;
  }
  return (
    <div className="w-10 h-10 rounded-md bg-neutral-border flex items-center justify-center">
      <FeatherImage className="w-4 h-4 text-subtext-color" />
    </div>
  );
}

function CollectionListRow({
  thumbnail_url,
  name,
  slug_or_id,
  momentum_label,
  subtitle,
  rightTop,
  rightBottom,
}: {
  thumbnail_url: string | null;
  name: string;
  slug_or_id: string;
  momentum_label: MomentumLabel;
  subtitle: React.ReactNode;
  rightTop: React.ReactNode;
  rightBottom: React.ReactNode;
}) {
  return (
    <Link
      to={`/collections/${slug_or_id}`}
      className="flex items-center gap-3 p-3 rounded-md hover:bg-default-background transition-colors border border-solid border-neutral-border"
    >
      <CollectionImage url={thumbnail_url} name={name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-body font-body font-medium text-default-font truncate">
            {name}
          </span>
          <Badge variant={momentumVariant[momentum_label]}>{momentum_label}</Badge>
        </div>
        <div className="flex items-center gap-3 text-caption font-caption text-subtext-color">
          {subtitle}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        {rightTop}
        {rightBottom}
      </div>
    </Link>
  );
}

function HoldingRow({ holding }: { holding: CollectionHolding }) {
  return (
    <CollectionListRow
      thumbnail_url={holding.thumbnail_url}
      name={holding.collection_name}
      slug_or_id={holding.collection_slug || holding.collection_id}
      momentum_label={holding.momentum_label}
      subtitle={
        <>
          <span>{holding.count} NFT{holding.count !== 1 ? 's' : ''}</span>
          {holding.floor_price != null && <span>Floor: {formatVolume(holding.floor_price)}</span>}
        </>
      }
      rightTop={
        <span className="text-body font-body font-medium text-default-font">
          {formatVolume(holding.estimated_value)}
        </span>
      }
      rightBottom={
        <span className="text-caption font-caption text-subtext-color">
          {holding.weight.toFixed(1)}% of portfolio
        </span>
      }
    />
  );
}

function MarketPulseRow({ collection }: { collection: MarketPulseCollection }) {
  return (
    <CollectionListRow
      thumbnail_url={collection.thumbnail_url}
      name={collection.collection_name}
      slug_or_id={collection.collection_slug || collection.collection_id}
      momentum_label={collection.momentum_label}
      subtitle={<span>{collection.signal}</span>}
      rightTop={
        collection.floor_price != null ? (
          <div className="flex items-center gap-2">
            <span className="text-body font-body font-medium text-default-font">
              {formatVolume(collection.floor_price)}
            </span>
            <span className={`text-caption font-caption font-medium ${
              collection.floor_change_24h > 0 ? 'text-success-700' : collection.floor_change_24h < 0 ? 'text-error-700' : 'text-subtext-color'
            }`}>
              {collection.floor_change_24h > 0 ? '+' : ''}{collection.floor_change_24h.toFixed(1)}%
            </span>
          </div>
        ) : null
      }
      rightBottom={
        <span className="text-caption font-caption text-subtext-color">
          Vol: {formatVolume(collection.volume_24h)}
        </span>
      }
    />
  );
}

function SectionHeader({ title, icon, count }: { title: string; icon?: React.ReactNode; count?: number }) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <h2 className="text-heading-3 font-heading-3 text-default-font">{title}</h2>
      {count !== undefined && count > 0 && (
        <Badge variant="neutral">{count}</Badge>
      )}
    </div>
  );
}

export const IntelligencePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('market');
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [marketPulse, setMarketPulse] = useState<MarketPulseData | null>(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [marketLoading, setMarketLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuthStore();

  const loadMarketPulse = useCallback(async () => {
    if (marketPulse) return;
    setMarketLoading(true);
    setError(null);
    try {
      const response = await getMarketPulse();
      if (response.success && response.data) {
        setMarketPulse(response.data);
      }
    } catch (err) {
      console.error('Error loading market pulse:', err);
      setError('Failed to load market data');
    } finally {
      setMarketLoading(false);
    }
  }, [marketPulse]);

  const loadPortfolio = useCallback(async () => {
    if (!isAuthenticated || portfolio) return;
    setPortfolioLoading(true);
    setError(null);
    try {
      const response = await getPortfolioIntelligence();
      if (response.success && response.data) {
        setPortfolio(response.data);
      }
    } catch (err) {
      console.error('Error loading portfolio:', err);
      setError('Failed to load portfolio data');
    } finally {
      setPortfolioLoading(false);
    }
  }, [isAuthenticated, portfolio]);

  useEffect(() => {
    if (activeTab === 'market') {
      loadMarketPulse();
    } else {
      loadPortfolio();
    }
  }, [activeTab, loadMarketPulse, loadPortfolio]);

  const loading = activeTab === 'market' ? marketLoading : portfolioLoading;

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Header */}
        <div className="flex flex-col gap-2 w-full">
          <h1 className="text-heading-2 font-heading-2 text-default-font">
            Intelligence
          </h1>
          <p className="text-body font-body text-subtext-color">
            Real-time market momentum and portfolio health analytics.
          </p>
        </div>

        {/* Tabs */}
        <Tabs>
          <Tabs.Item active={activeTab === 'market'} onClick={() => setActiveTab('market')}>
            Market Pulse
          </Tabs.Item>
          <Tabs.Item active={activeTab === 'portfolio'} onClick={() => setActiveTab('portfolio')}>
            Portfolio Health
          </Tabs.Item>
        </Tabs>

        {/* Loading */}
        {loading && (
          <div className="flex w-full items-center justify-center py-12">
            <LoadingSpinner size="md" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <Alert variant="error" title="Error" description={error} />
        )}

        {/* Market Pulse Tab */}
        {!loading && !error && activeTab === 'market' && marketPulse && (
          <div className="flex flex-col gap-6 w-full">
            {/* Market Overview Cards */}
            <div className="grid grid-cols-4 mobile:grid-cols-2 gap-4">
              <StatCard label="24h Volume">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {formatVolume(marketPulse.overview.total_volume_24h)}
                </span>
              </StatCard>
              <StatCard label="24h Sales">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {marketPulse.overview.total_sales_24h.toLocaleString()}
                </span>
              </StatCard>
              <StatCard label="Active Collections">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {marketPulse.overview.active_collections}
                </span>
              </StatCard>
              <StatCard label="Market Sentiment">
                <div className="flex flex-col gap-1">
                  <span className={`text-heading-3 font-heading-3 ${
                    marketPulse.overview.market_sentiment === 'bullish' ? 'text-success-700' :
                    marketPulse.overview.market_sentiment === 'bearish' ? 'text-error-700' :
                    'text-default-font'
                  }`}>
                    {marketPulse.overview.market_sentiment.charAt(0).toUpperCase() + marketPulse.overview.market_sentiment.slice(1)}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {sentimentDescription[marketPulse.overview.market_sentiment]}
                  </span>
                </div>
              </StatCard>
            </div>

            {/* Surging */}
            {marketPulse.surging.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader title="Surging" icon={<FeatherTrendingUp className="h-5 w-5 text-success-700" />} count={marketPulse.surging.length} />
                <div className="flex flex-col gap-2">
                  {marketPulse.surging.map(c => (
                    <MarketPulseRow key={c.collection_id} collection={c} />
                  ))}
                </div>
              </div>
            )}

            {/* Rising */}
            {marketPulse.rising.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader title="Rising" icon={<FeatherTrendingUp className="h-5 w-5 text-success-700" />} count={marketPulse.rising.length} />
                <div className="flex flex-col gap-2">
                  {marketPulse.rising.map(c => (
                    <MarketPulseRow key={c.collection_id} collection={c} />
                  ))}
                </div>
              </div>
            )}

            {/* Highest Volume */}
            {marketPulse.highest_volume.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader title="Highest Volume (24h)" count={marketPulse.highest_volume.length} />
                <div className="flex flex-col gap-2">
                  {marketPulse.highest_volume.map(c => (
                    <MarketPulseRow key={c.collection_id} collection={c} />
                  ))}
                </div>
              </div>
            )}

            {/* Newly Active */}
            {marketPulse.newly_active.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader title="Newly Active" count={marketPulse.newly_active.length} />
                <p className="text-caption font-caption text-subtext-color">
                  Collections with unusual activity spikes today
                </p>
                <div className="flex flex-col gap-2">
                  {marketPulse.newly_active.map(c => (
                    <MarketPulseRow key={c.collection_id} collection={c} />
                  ))}
                </div>
              </div>
            )}

            {/* Declining */}
            {marketPulse.declining.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader title="Declining" icon={<FeatherTrendingDown className="h-5 w-5 text-warning-700" />} count={marketPulse.declining.length} />
                <div className="flex flex-col gap-2">
                  {marketPulse.declining.map(c => (
                    <MarketPulseRow key={c.collection_id} collection={c} />
                  ))}
                </div>
              </div>
            )}

            {/* Crashing */}
            {marketPulse.crashing.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader title="Crashing" icon={<FeatherTrendingDown className="h-5 w-5 text-error-700" />} count={marketPulse.crashing.length} />
                <div className="flex flex-col gap-2">
                  {marketPulse.crashing.map(c => (
                    <MarketPulseRow key={c.collection_id} collection={c} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Portfolio Tab - not authenticated */}
        {!loading && !error && activeTab === 'portfolio' && !isAuthenticated && (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
            <span className="text-body font-body text-subtext-color">
              Connect your wallet to view portfolio intelligence.
            </span>
          </div>
        )}

        {/* Portfolio Tab - authenticated */}
        {!loading && !error && activeTab === 'portfolio' && isAuthenticated && portfolio && (
          <div className="flex flex-col gap-6 w-full">
            {/* Health Score Overview */}
            <div className="flex flex-col gap-4 rounded-lg border border-solid border-neutral-border p-6 bg-default-background">
              <h2 className="text-heading-3 font-heading-3 text-default-font">Portfolio Health</h2>
              <div className="flex flex-col gap-8 py-2">
                <div className="flex justify-center">
                  <CircularGauge score={portfolio.health_score.overall} label="Overall" infoKey="overall" size={128} />
                </div>
                <div className="grid grid-cols-4 mobile:grid-cols-2 gap-6 justify-items-center">
                  <CircularGauge score={portfolio.health_score.diversification} label="Diversification" infoKey="diversification" />
                  <CircularGauge score={portfolio.health_score.liquidity} label="Liquidity" infoKey="liquidity" />
                  <CircularGauge score={portfolio.health_score.momentum} label="Momentum" infoKey="momentum" />
                  <CircularGauge score={portfolio.health_score.valueAtRisk} label="Value Safety" infoKey="valueAtRisk" />
                </div>
              </div>
            </div>

            {/* Portfolio Summary Cards */}
            <div className="grid grid-cols-4 mobile:grid-cols-2 gap-4">
              <StatCard label="Total NFTs">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {portfolio.total_nfts}
                </span>
              </StatCard>
              <StatCard label="Estimated Value">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {formatVolume(portfolio.total_estimated_value)}
                </span>
              </StatCard>
              <StatCard label="Floor Value">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {formatVolume(portfolio.total_floor_value)}
                </span>
              </StatCard>
              <StatCard label="Collections">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {portfolio.unique_collections}
                </span>
              </StatCard>
            </div>

            {/* Concentration Alerts */}
            {portfolio.concentration_alerts.length > 0 && (
              <div className="flex flex-col gap-3">
                <h2 className="text-heading-3 font-heading-3 text-default-font">Alerts</h2>
                <div className="flex flex-col gap-2">
                  {portfolio.concentration_alerts.map((alert, i) => (
                    <Alert
                      key={i}
                      variant={alert.severity === 'critical' ? 'error' : 'warning'}
                      title={alert.severity === 'critical' ? 'Critical' : 'Warning'}
                      description={alert.message}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Top Gainers and Losers */}
            {(portfolio.top_gainers.length > 0 || portfolio.top_losers.length > 0) && (
              <div className="grid grid-cols-2 mobile:grid-cols-1 gap-6">
                {portfolio.top_gainers.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <FeatherTrendingUp className="h-5 w-5 text-success-700" />
                      <h2 className="text-heading-3 font-heading-3 text-success-700">Top Gainers</h2>
                    </div>
                    <div className="flex flex-col gap-2">
                      {portfolio.top_gainers.map(h => (
                        <HoldingRow key={h.collection_id} holding={h} />
                      ))}
                    </div>
                  </div>
                )}
                {portfolio.top_losers.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <FeatherTrendingDown className="h-5 w-5 text-error-700" />
                      <h2 className="text-heading-3 font-heading-3 text-error-700">Top Losers</h2>
                    </div>
                    <div className="flex flex-col gap-2">
                      {portfolio.top_losers.map(h => (
                        <HoldingRow key={h.collection_id} holding={h} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No momentum changes placeholder */}
            {portfolio.top_gainers.length === 0 && portfolio.top_losers.length === 0 && portfolio.holdings.length > 0 && (
              <div className="flex items-center gap-3 rounded-lg border border-solid border-neutral-border p-4 bg-default-background">
                <FeatherInfo className="h-5 w-5 text-subtext-color flex-shrink-0" />
                <span className="text-body font-body text-subtext-color">
                  No momentum changes detected in your holdings. All collections are currently stable.
                </span>
              </div>
            )}

            {/* Full Holdings Breakdown */}
            {portfolio.holdings.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-heading-3 font-heading-3 text-default-font">All Holdings</h2>
                  <InfoTooltip text="Each row shows the collection name, its momentum trend, how many NFTs you hold, and the floor price. On the right: the estimated total value of your holdings in that collection, and what percentage of your total portfolio it represents." />
                </div>
                {/* Column headers */}
                <div className="flex items-center gap-3 px-3 text-caption font-caption text-subtext-color">
                  <div className="w-10" />
                  <div className="flex-1">Collection</div>
                  <div className="flex flex-col items-end">
                    <span>Est. Value</span>
                    <span>Weight</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {portfolio.holdings.map(h => (
                    <HoldingRow key={h.collection_id} holding={h} />
                  ))}
                </div>
              </div>
            )}

            {portfolio.total_nfts === 0 && (
              <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
                <span className="text-body font-body text-subtext-color">
                  No NFTs found in this wallet.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
